// Sidebar — persistent left nav (Phase 2 of the nav redesign).
//
// Desktop (lg breakpoint ≥1024px): fixed 240px left column showing
// every section from the tool registry, each with its tools listed
// underneath. Sections are collapsible; state persists in
// localStorage so the user's preferred layout survives a reload.
//
// Mobile: the component is NOT rendered directly. The same markup
// lives in MobileNav's Sheet — keeping one set of JSX would require
// passing a closeFn through every tool link, so instead MobileNav
// imports and renders <SidebarNav> inside its Sheet content (see
// src/components/MobileNav.tsx).
//
// Why a single registry-driven nav:
//   - Adding tool #30 only requires editing src/data/tools.ts.
//     Neither the sidebar, the command palette, nor future hub
//     pages need a code change.
//   - Active-state highlighting uses the `isPathActive` helper so
//     tools with nested routes (e.g. /data-integrations/schema
//     under /data-integrations) still highlight correctly.

import { useState, useEffect } from "react";
import { NavLink } from "@/components/NavLink";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  Beef,
  Calculator,
  CalendarClock,
  ChevronDown,
  Droplets,
  Factory,
  LayoutDashboard,
  LineChart,
  Package,
  Server,
  Shield,
  Sparkles,
  Tractor,
  Truck,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  TOOL_SECTIONS,
  TOOLS,
  type ToolSectionId,
} from "@/data/tools";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { canAccessTier, tierForPath } from "@/data/tierAccess";

const SECTION_ICONS: Record<ToolSectionId, LucideIcon> = {
  dashboard: LayoutDashboard,
  "feeder-market": Truck,
  "fed-cattle": Factory,
  "boxed-beef": Package,
  wasde: CalendarClock,
  "cull-bulls": Beef,
  "cow-calf": Tractor,
  slaughter: Activity,
  "drought-forage": Droplets,
  "cattle-cycle": LineChart,
  "my-operation": Calculator,
  insights: Sparkles,
  admin: Server,
  "system-admin": Shield,
};

const COLLAPSED_SECTIONS_KEY = "beef-pulse-sidebar-collapsed";

function loadCollapsed(): Set<ToolSectionId> {
  try {
    const raw = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed as ToolSectionId[]);
  } catch {
    return new Set();
  }
}

function saveCollapsed(set: Set<ToolSectionId>) {
  try {
    localStorage.setItem(
      COLLAPSED_SECTIONS_KEY,
      JSON.stringify(Array.from(set)),
    );
  } catch {
    // localStorage unavailable — silently skip.
  }
}

/**
 * Returns true if the current pathname should mark the given path
 * as active. Uses startsWith for non-exact paths so a nested route
 * like /data-integrations/schema highlights the /data-integrations
 * parent *and* its own child entry.
 */
function isPathActive(pathname: string, path: string): boolean {
  if (path === "/dashboard") return pathname === "/dashboard";
  return pathname === path || pathname.startsWith(path + "/");
}

/**
 * Inner sidebar nav — reusable between the desktop persistent rail
 * and the mobile Sheet. `onNavigate` is called when any tool link
 * is clicked (used by mobile to close the sheet).
 */
export function SidebarNav({
  onNavigate,
  className,
}: {
  onNavigate?: () => void;
  className?: string;
}) {
  const { pathname } = useLocation();
  const { tier } = useSubscriptionTier();
  const { isAdmin } = useIsAdmin();
  const [collapsed, setCollapsed] = useState<Set<ToolSectionId>>(() =>
    loadCollapsed(),
  );

  useEffect(() => {
    saveCollapsed(collapsed);
  }, [collapsed]);

  // Tier-filter the tool registry so above-tier tools never render in
  // the sidebar. Hubs auto-hide when all their children are filtered
  // out. While tier is still loading we show nothing rather than
  // flashing the full nav and then hiding things.
  const visibleTools = TOOLS.filter((t) => {
    if (!tier) return false;
    // adminOnly tools (the system-admin section) are hidden from
    // anyone not in user_roles with role='admin'. The page itself
    // already enforces the gate; this filter just keeps the tools
    // out of the sidebar for non-admins.
    if (t.adminOnly && !isAdmin) return false;
    const required = tierForPath(t.path);
    return canAccessTier(tier, required);
  });

  // Ensure the section containing the active route is always expanded,
  // even if the user had collapsed it previously. Prevents the "where
  // did my tool go?" confusion after navigating from the command palette.
  const activeTool = visibleTools.find((t) => isPathActive(pathname, t.path));
  const forceExpanded = activeTool?.section;

  function toggleSection(id: ToolSectionId) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <nav
      className={cn(
        "flex flex-col py-2 overflow-y-auto text-[13px]",
        className,
      )}
      aria-label="Main navigation"
    >
      {TOOL_SECTIONS.map((section, idx) => {
        const tools = visibleTools.filter((t) => t.section === section.id);
        // Hide section if all tier-applicable children are filtered out.
        // BUT keep hub-only sections (zero registry tools by design + a
        // real hubPath) visible — the hub IS the tool. Cull & Bulls is
        // the canonical example: 0 entries in tools.ts because the
        // surface lives at /barn/:slug/preview (per-barn, not registry-
        // listable) plus the /cull-bulls hub itself.
        const registeredTotal = TOOLS.filter(
          (t) => t.section === section.id,
        ).length;
        const isHubOnly = registeredTotal === 0 && !!section.hubPath;
        if (tools.length === 0 && !isHubOnly) return null;

        const Icon = SECTION_ICONS[section.id];
        const isCollapsed =
          collapsed.has(section.id) && forceExpanded !== section.id;
        const isActiveTool = (path: string) => isPathActive(pathname, path);

        // Special case: Dashboard is a single-tool section. Render as
        // a flat link — no expand/collapse chevron, no nested duplicate.
        if (section.id === "dashboard" && tools.length === 1) {
          const t = tools[0];
          const active = isActiveTool(t.path);
          return (
            <NavLink
              key={section.id}
              to={t.path}
              end
              onClick={onNavigate}
              className={cn(
                "relative flex items-center gap-2.5 px-3 py-1.5 mx-1 rounded-md transition-colors duration-fast",
                active
                  ? "bg-sidebar-accent text-foreground font-semibold after:absolute after:left-0 after:top-1.5 after:bottom-1.5 after:w-[3px] after:bg-primary after:rounded-r-sm"
                  : "text-foreground-subtle hover:bg-sidebar-accent/60 hover:text-foreground",
              )}
            >
              <Icon
                className={cn(
                  "h-3.5 w-3.5 shrink-0",
                  active ? "text-primary" : "text-muted-foreground",
                )}
              />
              <span className="truncate">{t.name}</span>
            </NavLink>
          );
        }

        return (
          <div
            key={section.id}
            className={cn(
              "flex flex-col",
              // Subtle hairline above every section after the first, so
              // the eye can find section boundaries without the labels
              // needing to shout.
              idx > 0 && "mt-1 pt-1 border-t border-border/50",
            )}
          >
            {/* Section header — clicking the label/icon navigates to
                the hub landing page; a separate chevron toggles
                collapse. Two affordances, one row. */}
            <div className="flex items-center mx-1 group">
              {section.hubPath ? (
                <Link
                  to={section.hubPath}
                  onClick={onNavigate}
                  className={cn(
                    "flex items-center gap-2 flex-1 min-w-0 px-3 py-1 rounded-sm transition-colors text-left",
                    isPathActive(pathname, section.hubPath)
                      ? "text-foreground"
                      : "text-muted-foreground/70 hover:text-foreground",
                  )}
                  title={`${section.label} overview`}
                >
                  <Icon className="h-3 w-3 shrink-0 opacity-70" />
                  <span className="flex-1 text-[10px] font-semibold uppercase tracking-[0.08em] truncate">
                    {section.label}
                  </span>
                </Link>
              ) : (
                <div
                  className={cn(
                    "flex items-center gap-2 flex-1 min-w-0 px-3 py-1",
                    "text-muted-foreground/70",
                  )}
                >
                  <Icon className="h-3 w-3 shrink-0 opacity-70" />
                  <span className="flex-1 text-[10px] font-semibold uppercase tracking-[0.08em] truncate">
                    {section.label}
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                className={cn(
                  "p-1 rounded-sm transition-colors text-muted-foreground/50",
                  "hover:bg-accent/40 hover:text-foreground",
                )}
                aria-expanded={!isCollapsed}
                aria-label={isCollapsed ? "Expand" : "Collapse"}
              >
                <ChevronDown
                  className={cn(
                    "h-3 w-3 transition-transform",
                    isCollapsed && "-rotate-90",
                  )}
                />
              </button>
            </div>
            {!isCollapsed && (
              <div className="flex flex-col py-0.5">
                {tools.map((tool) => {
                  const active = isActiveTool(tool.path);
                  return (
                    <NavLink
                      key={tool.id}
                      to={tool.path}
                      end={tool.path === "/dashboard"}
                      onClick={onNavigate}
                      className={cn(
                        "relative block pl-[34px] pr-3 py-1 mx-1 rounded-md transition-colors duration-fast truncate",
                        active
                          ? "bg-sidebar-accent text-foreground font-semibold after:absolute after:left-0 after:top-1 after:bottom-1 after:w-[3px] after:bg-primary after:rounded-r-sm"
                          : "text-foreground-subtle hover:bg-sidebar-accent/60 hover:text-foreground",
                      )}
                      title={tool.description}
                    >
                      {tool.name}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

/**
 * Desktop sidebar wrapper — persistent left rail, visible only at
 * lg breakpoint and above. Mobile uses MobileNav's Sheet instead.
 */
export function Sidebar() {
  return (
    <aside className="hidden lg:block w-[220px] shrink-0 border-r border-sidebar-border bg-sidebar sticky top-12 self-start h-[calc(100vh-3rem)] overflow-hidden">
      <SidebarNav className="h-full" />
    </aside>
  );
}
